import { useState, useEffect, useRef } from "react";
import { useStateValue } from "../../context/StateProvider";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { storage } from "../../firebase/firebaseConfig";
import { Camera, Eye, EyeOff } from "lucide-react";
import {
  changeUserPassword,
  getAllusers,
  updateUserProfile,
} from "../../utils/firebasefunctions";
import { toast } from "react-toastify";
import { actionType } from "../../context/reducer";
import PageHeader from "../PageHeader";

function MainProfile() {
  const [{ user }, dispatch] = useStateValue();
  const [activeTab, setActiveTab] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [confirmShowPassword, setConfirmShowPassword] = useState(false);
  const [NewShowPassword, setNewShowPassword] = useState(false);

  const [profile, setProfile] = useState({
    firstname: "",
    lastname: "",
    email: "",
    phonenumber: "",
    profilepicture: "",
  });

  const [profileImage, setProfileImage] = useState(""); // preview URL
  const [profileImageFile, setProfileImageFile] = useState(null); // file for upload
  const [isLoading, setIsLoading] = useState(false); // file for upload
  const [isPasswordLoading, setPasswordIsLoading] = useState(false); // file for upload
  const fileInputRef = useRef(null);

  const [passwords, setPasswords] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleCurrentPasswordVisibility = () => {
    setNewShowPassword(!NewShowPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setConfirmShowPassword(!confirmShowPassword);
  };

  useEffect(() => {
    if (user) {
      setProfile({
        firstname: user.firstname || "",
        lastname: user.lastname || "",
        email: user.email || "",
        phonenumber: user.phonenumber || "",
        profilepicture: user.profilepicture || "",
      });
      setProfileImage(user.profilepicture || "");
    }
  }, [user]);

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfile((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswords((prev) => ({ ...prev, [name]: value }));
  };

  // Handle image selection & preview
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setProfileImageFile(file); // save file for upload

    const reader = new FileReader();
    reader.onload = (event) => {
      setProfileImage(event.target.result); // show preview
    };
    reader.readAsDataURL(file);
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    if (profile.firstname === "") {
      toast.warning("Required first name");
      return;
    } else if (profile.lastname === "") {
      toast.warning("Required last name");
      return;
    } else if (profile.phonenumber === "") {
      toast.warning("Required phone number");
      return;
    } 
    setIsLoading(true);
    try {
      let uploadedURL = profile.profilepicture;

      // Only upload if a new file is selected
      if (profileImageFile) {
        const storageRef = ref(
          storage,
          `profile_pictures/${user.id}_${profileImageFile.name}`
        );
        const uploadTask = uploadBytesResumable(storageRef, profileImageFile);

        uploadedURL = await new Promise((resolve, reject) => {
          uploadTask.on(
            "state_changed",
            () => {},
            (error) => reject(error),
            async () => {
              const url = await getDownloadURL(uploadTask.snapshot.ref);
              resolve(url);
            }
          );
        });
      }

      const updatedProfile = {
        ...user,
        firstname: profile.firstname,
        lastname: profile.lastname,
        phonenumber: profile.phonenumber,
        profilepicture: uploadedURL,
        email: user.email, // always original email
      };

      console.log('updatedProfile', updatedProfile)

      // Update in Firebase
      await updateUserProfile(user.id, updatedProfile);

      // Dispatch to context
      getallusers()

      // Reset file input
      setProfileImageFile(null);
      setProfileImage(uploadedURL);
      setIsLoading(false);
      toast.success("Profile updated successfully!");
    } catch (error) {
      console.error(error);
      toast.error("Error updating profile");
      setIsLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (passwords.currentPassword === "") {
      toast.warning("Required current password");
      return;
    } else if (passwords.newPassword === "") {
      toast.warning("Required new password");
      return;
    } else if (passwords.confirmPassword === "") {
      toast.warning("Required confirm password");
      return;
    } else if (passwords.newPassword !== passwords.confirmPassword) {
      toast.warning("Passwords do not match");
      return;
    }
    setPasswordIsLoading(true);
    try {
      await changeUserPassword(
        user.email,
        passwords.currentPassword,
        passwords.newPassword
      );
      toast.success("Password changed successfully!");
      setPasswordIsLoading(false);
      setPasswords({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (error) {
      console.error(error);
      toast.error("Error changing password");
      setPasswordIsLoading(false);
    }
  };

  async function getallusers() {
      await getAllusers("users")
        .then(async (response) => {
          dispatch({
            type: actionType.STORE_ALLUSERS,
            teamleads: response.filter(function (doc) {
              return doc.role == "Admin";
            }),
            clients: response.filter(function (doc) {
              return doc.role == "Supervisor";
            }),
            employees: response.filter(function (doc) {
              return doc.role == "Employee";
            }),
            allusers: response,
          });
        })
        .catch((err) => {
          alert(err);
        });
    }

  const tabs = ["Profile", "Change Password"];

  return (
    <div className="mb-5 lg:mb-0">
      <PageHeader title="Account Settings" />
      <div className="flex items-center justify-center p-4">
        <div className="rounded-xl w-full lg:w-[80%] mx-auto lg:p-6">
          {/* Tabs */}
          <div className="border-b border-gray-300 mb-6 flex justify-around">
            {tabs.map((tab, index) => (
              <button
                key={index}
                onClick={() => setActiveTab(index)}
                className={`pb-3 font-medium transition-colors ${
                  activeTab === index
                    ? "border-b-2 border-blue-500 text-blue-500"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Profile Tab */}
          {activeTab === 0 && (
            <form
              onSubmit={handleProfileSubmit}
              className="space-y-4 lg:w-[80%] mx-auto"
            >
              {/* Profile Picture */}
              <div className="flex justify-center mb-4">
                <div
                  className="w-28 h-28 lg:w-40 lg:h-40 rounded-full border-2 border-gray-200 relative cursor-pointer bg-white "
                  onClick={() => fileInputRef.current.click()}
                >
                  {profileImage ? (
                    <img
                      src={profileImage}
                      alt="Profile"
                      className="w-full h-full object-cover rounded-full"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-12 h-12 lg:w-20 lg:h-20 text-gray-400"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"
                        />
                      </svg>
                    </div>
                  )}
                  <div className="absolute top-0 right-1 bg-indigo-500 p-2 rounded-full z-50 hover:bg-indigo-600 transition">
                    <Camera className="text-white" size={20} />
                  </div>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  ref={fileInputRef}
                  onChange={handleImageChange}
                />
              </div>

              {/* Profile Fields */}
              <input
                type="text"
                name="firstname"
                value={profile.firstname}
                onChange={handleProfileChange}
                placeholder="First Name"
                className="w-full mt-1 text-sm rounded-lg bg-white px-5 py-3 text-darkColor placeholder:text-zinc-700/50"
              />
              <input
                type="text"
                name="lastname"
                value={profile.lastname}
                onChange={handleProfileChange}
                placeholder="Last Name"
                className="w-full mt-1 text-sm rounded-lg bg-white px-5 py-3 text-darkColor placeholder:text-zinc-700/50"
              />
              <input
                type="email"
                name="email"
                value={profile.email}
                placeholder="Email"
                disabled
                className="w-full mt-1 text-sm cursor-not-allowed rounded-lg bg-white/50 px-5 py-3 text-darkColor placeholder:text-zinc-700/50"
              />
              <input
                type="text"
                name="phonenumber"
                value={profile.phonenumber}
                onChange={handleProfileChange}
                placeholder="Phone Number"
                className="w-full mt-1 text-sm rounded-lg bg-white px-5 py-3 text-darkColor placeholder:text-zinc-700/50"
              />
              <div className="flex justify-center my-6 ">
                <button
                  type="submit"
                  className="text-base font-medium bg-themeColor shadow-md hover:bg-themeColor/90  mt-3 flex justify-center items-center text-white rounded-full py-[10px] px-20"
                >
                  Update Profile
                  {isLoading && (
                    <div role="status" className="pl-3">
                      <svg
                        aria-hidden="true"
                        class="w-6 h-6 text-gray-200 animate-spin dark:text-white fill-indigo-600"
                        viewBox="0 0 100 101"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                          fill="currentColor"
                        />
                        <path
                          d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                          fill="currentFill"
                        />
                      </svg>
                      <span class="sr-only">Loading...</span>
                    </div>
                  )}
                </button>
              </div>
            </form>
          )}

          {/* Change Password Tab */}
          {activeTab === 1 && (
            <form
              onSubmit={handlePasswordSubmit}
              className="space-y-4 lg:w-[80%] mx-auto"
            >
              <div className="relative">
                <input
                  type={NewShowPassword ? "text" : "password"}
                  name="currentPassword"
                  value={passwords.currentPassword}
                  onChange={handlePasswordChange}
                  placeholder="Current Password"
                  className="w-full mt-1 text-sm rounded-lg bg-white px-5 py-3 text-darkColor placeholder:text-zinc-700/50"
                />
                <div
                  className="absolute right-3 top-4 text-stone-950/40 cursor-pointer"
                  onClick={toggleCurrentPasswordVisibility}
                >
                  {NewShowPassword ? <Eye size={20} /> : <EyeOff size={20} />}
                </div>
              </div>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="newPassword"
                  value={passwords.newPassword}
                  onChange={handlePasswordChange}
                  placeholder="New Password"
                  className="w-full mt-1 text-sm rounded-lg bg-white px-5 py-3 text-darkColor placeholder:text-zinc-700/50"
                />
                <div
                  className="absolute right-3 top-4 text-stone-950/40 cursor-pointer"
                  onClick={togglePasswordVisibility}
                >
                  {showPassword ? <Eye size={20} /> : <EyeOff size={20} />}
                </div>
              </div>
              <div className="relative">
                <input
                  type={confirmShowPassword ? "text" : "password"}
                  name="confirmPassword"
                  value={passwords.confirmPassword}
                  onChange={handlePasswordChange}
                  placeholder="Confirm New Password"
                  className="w-full mt-1 text-sm rounded-lg bg-white px-5 py-3 text-darkColor placeholder:text-zinc-700/50"
                />
                <div
                  className="absolute right-3 top-4 text-stone-950/40 cursor-pointer"
                  onClick={toggleConfirmPasswordVisibility}
                >
                  {confirmShowPassword ? (
                    <Eye size={20} />
                  ) : (
                    <EyeOff size={20} />
                  )}
                </div>
              </div>
              <div className="flex justify-center my-6 ">
                <button
                  type="submit"
                  className="text-base font-medium bg-themeColor shadow-md hover:bg-themeColor/90  mt-3 flex justify-center items-center text-white rounded-full py-[10px] px-20"
                >
                  Change Password
                  {isPasswordLoading && (
                    <div role="status" className="pl-3">
                      <svg
                        aria-hidden="true"
                        class="w-6 h-6 text-gray-200 animate-spin dark:text-white fill-indigo-600"
                        viewBox="0 0 100 101"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                          fill="currentColor"
                        />
                        <path
                          d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                          fill="currentFill"
                        />
                      </svg>
                      <span class="sr-only">Loading...</span>
                    </div>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default MainProfile;
